/**
 * TypeGraph Error Hierarchy
 *
 * All errors extend TypeGraphError with:
 * - `code`: Machine-readable error code for programmatic handling
 * - `category`: Classification for error handling strategies
 * - `suggestion`: Optional recovery guidance for users
 * - `details`: Structured context about the error
 *
 * @example
 * ```typescript
 * try {
 *   await store.nodes.Person.create({ name: "" });
 * } catch (error) {
 *   if (isTypeGraphError(error)) {
 *     console.error(error.toUserMessage());
 *     if (isUserRecoverable(error)) {
 *       // Show to user for correction
 *     }
 *   }
 * }
 * ```
 */

import type { KindEntity } from "../core/types";
import type { SchemaDiff } from "../schema/migration";
// Type-only import: `materialize-indexes.ts` value-imports
// `ConfigurationError` from this file, but type-only imports are erased
// at runtime so this back-edge does not create a value cycle.
import type { MaterializeIndexesResult } from "../store/materialize-indexes";

// ============================================================
// Types
// ============================================================

/**
 * Error category for programmatic handling.
 *
 * - `user`: Caused by invalid input or incorrect usage. Recoverable by fixing input.
 * - `constraint`: Business rule or schema constraint violation. Recoverable by changing data.
 * - `system`: Internal error or infrastructure issue. May require investigation or retry.
 */
export type ErrorCategory = "user" | "constraint" | "system";

/**
 * Options for TypeGraphError constructor.
 */
export type TypeGraphErrorOptions = Readonly<{
  /** Structured context about the error */
  details?: Record<string, unknown>;
  /** Error category for handling strategies */
  category: ErrorCategory;
  /** Recovery guidance for users */
  suggestion?: string;
  /** Underlying cause of the error */
  cause?: unknown;
}>;

function formatCause(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.stack ?? cause.message;
  }
  if (typeof cause === "string") {
    return cause;
  }
  if (
    typeof cause === "number" ||
    typeof cause === "boolean" ||
    typeof cause === "bigint"
  ) {
    return String(cause);
  }
  if (typeof cause === "symbol") {
    return cause.description ?? "Symbol";
  }
  if (cause === undefined) {
    return "Unknown cause";
  }

  try {
    return JSON.stringify(cause);
  } catch (error) {
    return `Unserializable cause: ${
      error instanceof Error ? error.message : "Unknown error"
    }`;
  }
}

// ============================================================
// Base Error
// ============================================================

/**
 * Base error class for all TypeGraph errors.
 *
 * Provides structured error information for both programmatic handling
 * and user-friendly messages.
 */
export class TypeGraphError extends Error {
  /** Machine-readable error code (e.g., "VALIDATION_ERROR") */
  readonly code: string;

  /** Error category for handling strategies */
  readonly category: ErrorCategory;

  /** Structured context about the error */
  readonly details: Readonly<Record<string, unknown>>;

  /** Recovery guidance for users */
  readonly suggestion?: string;

  constructor(message: string, code: string, options: TypeGraphErrorOptions) {
    super(message, options.cause ? { cause: options.cause } : undefined);
    this.name = "TypeGraphError";
    this.code = code;
    this.category = options.category;
    this.details = Object.freeze(options.details ?? {});
    if (options.suggestion !== undefined) {
      this.suggestion = options.suggestion;
    }
  }

  /**
   * Returns a user-friendly error message with suggestion if available.
   */
  toUserMessage(): string {
    if (this.suggestion) {
      return `${this.message}\n\nSuggestion: ${this.suggestion}`;
    }
    return this.message;
  }

  /**
   * Returns a detailed string representation for logging.
   */
  toLogString(): string {
    const lines = [
      `[${this.code}] ${this.message}`,
      `  Category: ${this.category}`,
    ];

    if (this.suggestion) {
      lines.push(`  Suggestion: ${this.suggestion}`);
    }

    const detailKeys = Object.keys(this.details);
    if (detailKeys.length > 0) {
      lines.push(`  Details: ${JSON.stringify(this.details)}`);
    }

    if (this.cause) {
      lines.push(`  Cause: ${formatCause(this.cause)}`);
    }

    return lines.join("\n");
  }
}

// ============================================================
// Validation Errors (category: "user")
// ============================================================

/**
 * Validation issue from Zod or custom validation.
 */
export type ValidationIssue = Readonly<{
  /** Path to the invalid field (e.g., "address.city") */
  path: string;
  /** Human-readable error message */
  message: string;
  /** Zod error code if from Zod validation */
  code?: string;
  /**
   * The identity assertion the issue is about, carried structurally so
   * consumers (e.g. interchange import error reporting) never have to parse
   * the human-readable message for it.
   */
  assertionId?: string;
}>;

/**
 * The stable {@link ValidationIssue.code} every inverted-validity-window
 * refusal carries, so a caller can recognize that specific failure without
 * matching on the message. `ValidationError`'s own code is the family-wide
 * `VALIDATION_ERROR`, so the discriminator lives on the issue — mirroring the
 * identity-import window refusal (`IDENTITY_IMPORT_INVALID_WINDOW`).
 *
 * A write carrying it was refused because `validTo` precedes the row's
 * effective `validFrom`: the row would have stopped being true before it
 * started, observable at no `asOf` coordinate at all. Zero-width windows
 * (`validTo === validFrom`) are legal and never raise it.
 */
export const INVERTED_VALIDITY_WINDOW_CODE = "INVERTED_VALIDITY_WINDOW";

/**
 * The stable {@link ValidationIssue.code} carried by a refusal to accept a
 * `validFrom` the write cannot apply: the target row is LIVE, and an in-place
 * update never rewrites a live row's lower bound — that bound is history.
 * `ValidationError`'s own code is the family-wide `VALIDATION_ERROR`, so the
 * discriminator lives on the issue — mirroring
 * {@link INVERTED_VALIDITY_WINDOW_CODE}.
 *
 * Only a bound naming a DIFFERENT instant than the row already stores raises it.
 * Restating the stored bound is legal and writes nothing new, and a resurrection
 * — which does rewrite `valid_from` — applies a stated bound rather than
 * refusing it.
 *
 * The message names both instants: the one stated and the one the row stores, so
 * the caller can restate the stored bound without a second read.
 */
export const IMMUTABLE_VALIDITY_LOWER_BOUND_CODE =
  "IMMUTABLE_VALIDITY_LOWER_BOUND";

/**
 * Stable {@link ValidationIssue.code} for an edge-id ownership mismatch.
 *
 * Edge ids are graph-global. A collection-scoped write therefore has to prove
 * that the stored row has the collection's kind and, when the caller supplies
 * endpoints, the same immutable endpoints. This refusal prevents a collection
 * from mutating another kind's row and prevents an upsert from silently
 * ignoring an attempted repoint.
 */
export const EDGE_IDENTITY_MISMATCH_CODE = "EDGE_IDENTITY_MISMATCH";

/**
 * The stable {@link ValidationIssue.code} every "this id is already taken"
 * create refusal carries, whichever way the create found out: its own existence
 * probe, or the engine refusing the INSERT. `ValidationError`'s own code is the
 * family-wide `VALIDATION_ERROR`, so the discriminator lives on the issue —
 * mirroring {@link INVERTED_VALIDITY_WINDOW_CODE}.
 *
 * `details.entityType` says whether a node or an edge was refused and
 * `details.kind` names its kind. `details.id` names the taken id, and is absent
 * only when the refused statement inserted MORE THAN ONE row: the engine reports
 * that the statement collided without saying which row did.
 */
export const ENTITY_ALREADY_EXISTS_CODE = "ENTITY_ALREADY_EXISTS";

/**
 * Details for ValidationError.
 */
export type ValidationErrorDetails = Readonly<{
  /** Type of entity being validated */
  entityType?: KindEntity;
  /** Kind/type name of the entity */
  kind?: string;
  /** Operation being performed */
  operation?: "create" | "update" | "delete" | "hardDelete";
  /** Entity ID if updating */
  id?: string;
  /** Individual validation issues */
  issues: readonly ValidationIssue[];
}>;

/**
 * Thrown when schema validation fails during node or edge operations.
 *
 * @example
 * ```typescript
 * try {
 *   await store.nodes.Person.create({ email: "invalid" });
 * } catch (error) {
 *   if (error instanceof ValidationError) {
 *     console.log(error.details.issues);
 *     // [{ path: "email", message: "Invalid email" }]
 *   }
 * }
 * ```
 */
export class ValidationError extends TypeGraphError {
  declare readonly details: ValidationErrorDetails;

  constructor(
    message: string,
    details: ValidationErrorDetails,
    options?: { cause?: unknown; suggestion?: string },
  ) {
    const fieldList =
      details.issues.length > 0 ?
        details.issues.map((index) => index.path || "(root)").join(", ")
      : "unknown";

    super(message, "VALIDATION_ERROR", {
      details,
      category: "user",
      suggestion:
        options?.suggestion ??
        `Check the following fields: ${fieldList}. See error.details.issues for specific validation failures.`,
      cause: options?.cause,
    });
    this.name = "ValidationError";
  }
}

// ============================================================
// Not Found Errors (category: "user")
// ============================================================

/**
 * Details for NodeNotFoundError.
 */
export type NodeNotFoundErrorDetails = Readonly<{
  kind: string;
  id: string;
}>;

/**
 * Thrown when a node is not found.
 *
 * @example
 * ```typescript
 * try {
 *   await store.nodes.Person.get("nonexistent-id");
 * } catch (error) {
 *   if (error instanceof NodeNotFoundError) {
 *     console.log(error.details.kind, error.details.id);
 *   }
 * }
 * ```
 */
export class NodeNotFoundError extends TypeGraphError {
  declare readonly details: NodeNotFoundErrorDetails;

  constructor(kind: string, id: string, options?: { cause?: unknown }) {
    super(`Node not found: ${kind}/${id}`, "NODE_NOT_FOUND", {
      details: { kind, id },
      category: "user",
      suggestion: `Verify the node ID "${id}" exists and has not been deleted.`,
      cause: options?.cause,
    });
    this.name = "NodeNotFoundError";
  }
}

/**
 * Details for EdgeNotFoundError.
 */
export type EdgeNotFoundErrorDetails = Readonly<{
  kind: string;
  id: string;
}>;

/**
 * Thrown when an edge is not found.
 */
export class EdgeNotFoundError extends TypeGraphError {
  declare readonly details: EdgeNotFoundErrorDetails;

  constructor(kind: string, id: string, options?: { cause?: unknown }) {
    super(`Edge not found: ${kind}/${id}`, "EDGE_NOT_FOUND", {
      details: { kind, id },
      category: "user",
      suggestion: `Verify the edge ID "${id}" exists and has not been deleted.`,
      cause: options?.cause,
    });
    this.name = "EdgeNotFoundError";
  }
}

/**
 * Details for KindNotFoundError.
 */
export type KindNotFoundErrorDetails = Readonly<{
  kindName: string;
  entity: KindEntity;
  graphId?: string;
}>;

/**
 * Thrown when an operation references a kind that isn't registered on
 * the graph — `getNodeCollectionOrThrow` against an unknown kind, a
 * `materializeIndexes({ kinds })` filter naming a missing kind, an
 * extension referencing an unresolved endpoint, search facade typos,
 * etc. Carries the offending `kindName` and `entity` plus the host
 * `graphId` so logs are unambiguous when multiple stores share a
 * process.
 */
export class KindNotFoundError extends TypeGraphError {
  declare readonly details: KindNotFoundErrorDetails;
  readonly kindName: string;
  readonly entity: KindEntity;

  constructor(
    kindName: string,
    entity: KindEntity,
    options?: Readonly<{
      graphId?: string;
      suggestion?: string;
      cause?: unknown;
    }>,
  ) {
    const where =
      options?.graphId === undefined ? "" : ` on graph "${options.graphId}"`;
    super(
      `${entity === "node" ? "Node" : "Edge"} kind "${kindName}" is not registered${where}.`,
      "KIND_NOT_FOUND",
      {
        details: {
          kindName,
          entity,
          ...(options?.graphId === undefined ?
            {}
          : { graphId: options.graphId }),
        },
        category: "user",
        suggestion:
          options?.suggestion ??
          "Compile-time kinds come from defineGraph; extension kinds appear after store.evolve() returns. Check store.introspect() for the registered set.",
        cause: options?.cause,
      },
    );
    this.name = "KindNotFoundError";
    this.kindName = kindName;
    this.entity = entity;
  }
}

export type RuntimeKindTokenFailure =
  | "invalid"
  | "wrong-entity"
  | "wrong-kind"
  | "wrong-store"
  | "stale"
  | "schema-mismatch"
  | "unreconciled";

/**
 * Refuses runtime-kind evidence that was forged, misapplied, or licensed for a
 * different reconciled store schema.
 */
export class RuntimeKindTokenError extends TypeGraphError {
  readonly reason: RuntimeKindTokenFailure;

  constructor(
    reason: RuntimeKindTokenFailure,
    entity: KindEntity,
    details: Readonly<Record<string, unknown>> = {},
  ) {
    const messages: Record<RuntimeKindTokenFailure, string> = {
      invalid: "Runtime-kind evidence is not a TypeGraph-issued token.",
      "wrong-entity": `Runtime-kind evidence does not describe a ${entity} kind.`,
      "wrong-kind": "Runtime-kind evidence was altered after it was issued.",
      "wrong-store":
        "Runtime-kind evidence belongs to a different Store instance.",
      stale:
        "Runtime-kind evidence does not match the Store's reconciled schema.",
      "schema-mismatch": `The supplied graph-extension definition does not match the registered ${entity} kind.`,
      unreconciled:
        "Runtime-kind evidence requires a Store with reconciled schema metadata.",
    };
    super(messages[reason], "RUNTIME_KIND_TOKEN_ERROR", {
      category: "user",
      details: { reason, entity, ...details },
      suggestion:
        reason === "stale" || reason === "wrong-store" ?
          "Mint a new token from the Store instance that will execute the operation."
        : "Mint tokens with store.runtimeNodeKind(...) or store.runtimeEdgeKind(...).",
    });
    this.name = "RuntimeKindTokenError";
    this.reason = reason;
  }
}

/**
 * Details for NodeConstraintNotFoundError.
 */
export type NodeConstraintNotFoundErrorDetails = Readonly<{
  constraintName: string;
  kind: string;
}>;

/**
 * Thrown when a uniqueness constraint name is not found on a node kind.
 */
export class NodeConstraintNotFoundError extends TypeGraphError {
  declare readonly details: NodeConstraintNotFoundErrorDetails;

  constructor(
    constraintName: string,
    kind: string,
    options?: { cause?: unknown },
  ) {
    super(
      `Constraint not found: "${constraintName}" on node kind "${kind}"`,
      "CONSTRAINT_NOT_FOUND",
      {
        details: { constraintName, kind },
        category: "user",
        suggestion: `Verify the constraint name "${constraintName}" is defined in the unique constraints for "${kind}".`,
        cause: options?.cause,
      },
    );
    this.name = "NodeConstraintNotFoundError";
  }
}

/**
 * Details for NodeIndexNotFoundError.
 */
export type NodeIndexNotFoundErrorDetails = Readonly<{
  indexName: string;
  kind: string;
}>;

/**
 * Thrown when a declared node index name is not found on a node kind.
 */
export class NodeIndexNotFoundError extends TypeGraphError {
  declare readonly details: NodeIndexNotFoundErrorDetails;

  constructor(indexName: string, kind: string, options?: { cause?: unknown }) {
    super(
      `Index not found: "${indexName}" on node kind "${kind}"`,
      "INDEX_NOT_FOUND",
      {
        details: { indexName, kind },
        category: "user",
        suggestion: `Verify the index name "${indexName}" is declared via defineNodeIndex for "${kind}" and passed to defineGraph({ indexes }).`,
        cause: options?.cause,
      },
    );
    this.name = "NodeIndexNotFoundError";
  }
}

// ============================================================
// Constraint Errors (category: "constraint")
// ============================================================

/**
 * Details for EndpointNotFoundError.
 */
export type EndpointNotFoundErrorDetails = Readonly<{
  edgeKind: string;
  endpoint: "from" | "to";
  nodeKind: string;
  nodeId: string;
}>;

/**
 * Thrown when edge endpoint node does not exist or is deleted.
 */
export class EndpointNotFoundError extends TypeGraphError {
  declare readonly details: EndpointNotFoundErrorDetails;

  constructor(
    details: EndpointNotFoundErrorDetails,
    options?: { cause?: unknown },
  ) {
    super(
      `Endpoint node not found for edge "${details.edgeKind}": ${details.nodeKind}/${details.nodeId} (${details.endpoint})`,
      "ENDPOINT_NOT_FOUND",
      {
        details,
        category: "constraint",
        suggestion: `Ensure the ${details.endpoint} node exists before creating the edge.`,
        cause: options?.cause,
      },
    );
    this.name = "EndpointNotFoundError";
  }
}

/**
 * Details for EndpointError.
 */
export type EndpointErrorDetails = Readonly<{
  edgeKind: string;
  endpoint: "from" | "to";
  actualKind: string;
  expectedKinds: readonly string[];
}>;

/**
 * Thrown when edge endpoint has wrong node type on "from" or "to".
 */
export class EndpointError extends TypeGraphError {
  declare readonly details: EndpointErrorDetails;

  constructor(details: EndpointErrorDetails, options?: { cause?: unknown }) {
    const expected = details.expectedKinds.join(" | ");
    super(
      `Invalid ${details.endpoint} endpoint for edge "${details.edgeKind}": got "${details.actualKind}", expected ${expected}`,
      "ENDPOINT_ERROR",
      {
        details,
        category: "constraint",
        suggestion: `Use a node of type ${expected} as the ${details.endpoint} endpoint.`,
        cause: options?.cause,
      },
    );
    this.name = "EndpointError";
  }
}

/**
 * Details for EndpointPairError.
 */
export type EndpointPairErrorDetails = Readonly<{
  edgeKind: string;
  endpoint: "pair";
  fromKind: string;
  toKind: string;
  allowedPairs: readonly Readonly<{ from: string; to: string }>[];
}>;

/**
 * Thrown when an edge connects an invalid/undeclared endpoint pair.
 */
export class EndpointPairError extends TypeGraphError {
  declare readonly details: EndpointPairErrorDetails;

  constructor(
    details: Readonly<{
      edgeKind: string;
      fromKind: string;
      toKind: string;
      allowedPairs: readonly Readonly<{ from: string; to: string }>[];
    }>,
    options?: { cause?: unknown },
  ) {
    const fullDetails: EndpointPairErrorDetails = {
      ...details,
      endpoint: "pair",
    };
    const allowed = fullDetails.allowedPairs
      .map((pair) => `(${pair.from} -> ${pair.to})`)
      .join(", ");
    super(
      `Invalid endpoint pair for edge "${fullDetails.edgeKind}": cannot connect source "${fullDetails.fromKind}" to target "${fullDetails.toKind}". Allowed pairs: [${allowed}]`,
      "ENDPOINT_PAIR_ERROR",
      {
        details: fullDetails,
        category: "constraint",
        suggestion: `Connect endpoints matching one of the allowed pairs: [${allowed}].`,
        cause: options?.cause,
      },
    );
    this.name = "EndpointPairError";
  }
}

/**
 * Details for UniquenessError.
 */
export type UniquenessErrorDetails = Readonly<{
  constraintName: string;
  kind: string;
  existingId: string;
  newId: string;
  fields: readonly string[];
  /**
   * The claim row's `node_kind` — the axis this violation was found at.
   * Optional so a third-party backend implementing the documented "throws
   * `UniquenessError`" contract keeps working unchanged; when a throw site
   * omits it, a caller loses only the disambiguation this field exists for,
   * never correctness of the refusal itself.
   *
   * `constraintName` alone cannot tell two disjoint pairs apart — every
   * `disjointWith` pair shares the single reserved `DISJOINT_CONSTRAINT_NAME`
   * — so `mapClaimRefusal` (`store/claims/node-claims.ts`) matches on this
   * field together with `constraintName` and the key, which is exactly the
   * claim row's primary key and therefore unambiguous.
   */
  axis?: string;
}>;

/**
 * Thrown when uniqueness constraint is violated.
 */
export class UniquenessError extends TypeGraphError {
  declare readonly details: UniquenessErrorDetails;

  constructor(details: UniquenessErrorDetails, options?: { cause?: unknown }) {
    const fieldList = details.fields.join(", ");
    super(
      `Uniqueness violation on "${details.kind}": constraint "${details.constraintName}" (fields: ${fieldList}) conflicts with existing node ${details.existingId}`,
      "UNIQUENESS_VIOLATION",
      {
        details,
        category: "constraint",
        suggestion: `Change the values for fields [${fieldList}] to be unique, or update the existing node ${details.existingId} instead.`,
        cause: options?.cause,
      },
    );
    this.name = "UniquenessError";
  }
}

/** A direct edge write collided with its schema-declared match identity. */
export class EdgeMatchIdentityConflictError extends TypeGraphError {
  constructor(
    details: Readonly<{
      attempted: readonly Readonly<{
        id: string;
        identityName: string;
        kind: string;
      }>[];
    }>,
    options?: Readonly<{ cause?: unknown }>,
  ) {
    super(
      "Edge write conflicts with an existing schema-declared match identity",
      "EDGE_MATCH_IDENTITY_CONFLICT",
      {
        category: "constraint",
        details,
        suggestion:
          "Use getOrCreateByEndpoints() to return the identity owner, or change the declared identity values.",
        cause: options?.cause,
      },
    );
    this.name = "EdgeMatchIdentityConflictError";
  }
}

/**
 * Details for CardinalityError.
 */
export type CardinalityErrorDetails = Readonly<{
  edgeKind: string;
  fromKind: string;
  fromId: string;
  cardinality: string;
  existingCount: number;
}>;

/**
 * Thrown when cardinality constraint is violated.
 */
export class CardinalityError extends TypeGraphError {
  declare readonly details: CardinalityErrorDetails;

  constructor(details: CardinalityErrorDetails, options?: { cause?: unknown }) {
    super(
      `Cardinality violation: "${details.edgeKind}" from ${details.fromKind}/${details.fromId} allows "${details.cardinality}" but ${details.existingCount} edge(s) already exist`,
      "CARDINALITY_ERROR",
      {
        details,
        category: "constraint",
        suggestion:
          details.cardinality === "one" || details.cardinality === "unique" ?
            `Delete the existing edge before creating a new one, or use cardinality "many".`
          : `Check if the cardinality constraint "${details.cardinality}" is correct for your use case.`,
        cause: options?.cause,
      },
    );
    this.name = "CardinalityError";
  }
}

/**
 * Details for DisjointError.
 */
export type DisjointErrorDetails = Readonly<{
  nodeId: string;
  attemptedKind: string;
  conflictingKind: string;
}>;

/**
 * Thrown when disjointness constraint is violated.
 *
 * Disjoint types cannot share the same ID - a node cannot be both
 * a Person and an Organization if they are declared disjoint.
 */
export class DisjointError extends TypeGraphError {
  declare readonly details: DisjointErrorDetails;

  constructor(details: DisjointErrorDetails, options?: { cause?: unknown }) {
    super(
      `Disjoint constraint violation: cannot create ${details.attemptedKind} with ID "${details.nodeId}" - conflicts with existing ${details.conflictingKind}`,
      "DISJOINT_ERROR",
      {
        details,
        category: "constraint",
        suggestion: `Use a different ID for the new ${details.attemptedKind} node, or delete the existing ${details.conflictingKind}/${details.nodeId} first.`,
        cause: options?.cause,
      },
    );
    this.name = "DisjointError";
  }
}

export type IdentityContradictionErrorDetails = Readonly<{
  /**
   * The identity write that could not stand. `merge` is the post-write
   * affected-class assertion a graph merge runs inside its commit
   * transaction, which names the whole merge rather than a single assertion
   * because the contradiction is a property of the state the merge would
   * leave behind.
   */
  operation: "assertSame" | "assertDifferent" | "fold" | "import" | "merge";
  a: Readonly<{ kind: string; id: string }>;
  b: Readonly<{ kind: string; id: string }>;
  reason: "different-assertion" | "same-class" | "disjoint-kinds";
  conflictingAssertionId?: string;
  conflictingKinds?: readonly [string, string];
}>;

/** Thrown when an identity mutation would make the ledger contradictory. */
export class IdentityContradictionError extends TypeGraphError {
  declare readonly details: IdentityContradictionErrorDetails;

  constructor(
    details: IdentityContradictionErrorDetails,
    options?: Readonly<{ cause?: unknown }>,
  ) {
    super(
      `Identity contradiction: ${details.a.kind}/${details.a.id} and ${details.b.kind}/${details.b.id} cannot satisfy ${details.operation}.`,
      "IDENTITY_CONTRADICTION",
      {
        details,
        category: "constraint",
        suggestion:
          "Retract the conflicting identity assertion or correct the graph ontology before retrying.",
        cause: options?.cause,
      },
    );
    this.name = "IdentityContradictionError";
  }
}

export type IdentityValidityWindowErrorDetails = Readonly<{
  reason:
    | "future-valid-from"
    | "future-valid-to"
    | "inverted"
    | "overlapping-open-window";
  validFrom: string;
  validTo?: string;
  operationInstant: string;
}>;

/** Thrown when an identity assertion validity window cannot be applied. */
export class IdentityValidityWindowError extends TypeGraphError {
  declare readonly details: IdentityValidityWindowErrorDetails;

  constructor(details: IdentityValidityWindowErrorDetails) {
    const code =
      details.reason === "future-valid-from" ? "IDENTITY_VALIDITY_FUTURE_START"
      : details.reason === "future-valid-to" ? "IDENTITY_VALIDITY_FUTURE_END"
      : details.reason === "inverted" ? "IDENTITY_VALIDITY_INVERTED"
      : "IDENTITY_VALIDITY_OPEN_WINDOW_CONFLICT";
    super(
      `Identity assertion validity window cannot be applied: ${details.reason}.`,
      code,
      {
        details,
        category:
          details.reason === "overlapping-open-window" ? "constraint" : "user",
        suggestion:
          details.reason === "inverted" ? "Pass validFrom at or before validTo."
          : details.reason === "overlapping-open-window" ?
            "Retract the existing open assertion before creating a different open validity window for the same relation and pair."
          : "Use a boundary at or before the current operation clock; future-scheduled identity transitions are not supported.",
      },
    );
    this.name = "IdentityValidityWindowError";
  }
}

export type IdentityEndpointValidityErrorDetails = Readonly<{
  endpoint: Readonly<{ kind: string; id: string }>;
  assertionWindow: Readonly<{ validFrom: string; validTo?: string }>;
  endpointWindow: Readonly<{
    validFrom?: string;
    validTo?: string;
    deletedAt?: string;
  }>;
}>;

/** Thrown when an identity endpoint does not exist throughout the assertion window. */
export class IdentityEndpointValidityError extends TypeGraphError {
  declare readonly details: IdentityEndpointValidityErrorDetails;

  constructor(details: IdentityEndpointValidityErrorDetails) {
    super(
      `Identity endpoint ${details.endpoint.kind}/${details.endpoint.id} does not exist throughout the requested assertion validity window.`,
      "IDENTITY_ENDPOINT_VALIDITY",
      {
        details,
        category: "constraint",
        suggestion:
          "Choose an assertion window contained by both endpoint validity windows.",
      },
    );
    this.name = "IdentityEndpointValidityError";
  }
}

export type IdentitySeparationViolationErrorDetails = Readonly<{
  graphId: string;
  /**
   * `"database"` when the CHECK constraint on the separation relation aborted
   * the write — the backstop firing as designed. `"writer"` when the write was
   * accepted, which means the relation was provisioned without its constraint
   * and only the writer's own re-check caught the contradiction.
   */
  enforcedBy: "database" | "writer";
  /** The single class key both endpoints collapsed onto. */
  classKey: string;
  /** The `different` assertion whose endpoints landed in one class. */
  assertionId: string;
  a: Readonly<{ kind: string; id: string }>;
  b: Readonly<{ kind: string; id: string }>;
}>;

/**
 * Thrown when the derived separation relation refuses a write that would place
 * both endpoints of a current `different` assertion in one identity class.
 *
 * This is the database-level backstop beneath the plan-time simulation and the
 * applier's validation. Reaching it means an earlier layer let a contradiction
 * through: the transaction is aborted rather than committed, but the layer that
 * should have refused it typed and early is worth investigating.
 */
export class IdentitySeparationViolationError extends TypeGraphError {
  declare readonly details: IdentitySeparationViolationErrorDetails;

  constructor(
    details: IdentitySeparationViolationErrorDetails,
    options?: Readonly<{ cause?: unknown }>,
  ) {
    super(
      `Identity separation violated: ${details.a.kind}/${details.a.id} and ${details.b.kind}/${details.b.id} are held apart by assertion ${details.assertionId}, but this write would put both in identity class ${details.classKey}.`,
      "IDENTITY_SEPARATION_VIOLATION",
      {
        details,
        category: "constraint",
        suggestion:
          "Retract the conflicting different assertion before merging these identity classes. If no application call requested this merge, the write reached the ledger through a path that skipped identity validation.",
        cause: options?.cause,
      },
    );
    this.name = "IdentitySeparationViolationError";
  }
}

/**
 * Details for RestrictedDeleteError.
 */
export type RestrictedDeleteErrorDetails = Readonly<{
  nodeKind: string;
  nodeId: string;
  edgeCount: number;
  edgeKinds: readonly string[];
}>;

/**
 * Thrown when deletion is blocked due to existing edges (restrict behavior).
 */
export class RestrictedDeleteError extends TypeGraphError {
  declare readonly details: RestrictedDeleteErrorDetails;

  constructor(
    details: RestrictedDeleteErrorDetails,
    options?: { cause?: unknown },
  ) {
    const edgeList = details.edgeKinds.join(", ");
    super(
      `Cannot delete ${details.nodeKind}/${details.nodeId}: ${details.edgeCount} connected edge(s) exist (${edgeList})`,
      "RESTRICTED_DELETE",
      {
        details,
        category: "constraint",
        suggestion: `Delete the connected edges first, or change onDelete behavior to "cascade" (auto-delete edges) or "disconnect" (soft-delete edges).`,
        cause: options?.cause,
      },
    );
    this.name = "RestrictedDeleteError";
  }
}

// ============================================================
// Concurrency Errors (category: "system")
// ============================================================

/**
 * Details for VersionConflictError.
 */
export type VersionConflictErrorDetails = Readonly<{
  kind: string;
  id: string;
  expectedVersion: number;
  actualVersion: number;
}>;

/**
 * Thrown when optimistic locking detects a concurrent modification.
 *
 * This occurs when two operations try to update the same entity simultaneously.
 * The operation with the stale version fails.
 */
export class VersionConflictError extends TypeGraphError {
  declare readonly details: VersionConflictErrorDetails;

  constructor(
    details: VersionConflictErrorDetails,
    options?: { cause?: unknown },
  ) {
    super(
      `Version conflict on ${details.kind}/${details.id}: expected version ${details.expectedVersion}, found ${details.actualVersion}`,
      "VERSION_CONFLICT",
      {
        details,
        category: "system",
        suggestion: `Fetch the latest version of the entity and retry the operation. This error indicates concurrent modification.`,
        cause: options?.cause,
      },
    );
    this.name = "VersionConflictError";
  }
}

/**
 * Details for TransactionConflictError.
 */
export type TransactionConflictErrorDetails = Readonly<{
  operation: string;
  attempts: number;
}>;

/**
 * Thrown when a transaction was aborted by a serialization failure or
 * deadlock on every attempt available to it.
 *
 * The failing driver error — the last attempt's — is preserved as `cause`.
 * PostgreSQL's own protocol for both conditions is to re-run the whole
 * transaction from the top, which is what exhausted the attempt budget here.
 */
export class TransactionConflictError extends TypeGraphError {
  declare readonly details: TransactionConflictErrorDetails;

  constructor(
    details: TransactionConflictErrorDetails,
    options?: { cause?: unknown },
  ) {
    super(
      `Transaction conflict on ${details.operation}: aborted by a serialization failure or deadlock after ${details.attempts} attempt(s)`,
      "TRANSACTION_CONFLICT",
      {
        details,
        category: "system",
        suggestion: "Re-run the whole transaction from the top.",
        cause: options?.cause,
      },
    );
    this.name = "TransactionConflictError";
  }
}

// ============================================================
// Schema Errors (category: "system")
// ============================================================

/**
 * Details for SchemaMismatchError.
 */
export type SchemaMismatchErrorDetails = Readonly<{
  graphId: string;
  expectedHash: string;
  actualHash: string;
}>;

/**
 * Thrown when the schema in code doesn't match the schema in the database.
 */
export class SchemaMismatchError extends TypeGraphError {
  declare readonly details: SchemaMismatchErrorDetails;

  constructor(
    details: SchemaMismatchErrorDetails,
    options?: { cause?: unknown },
  ) {
    super(
      `Schema mismatch for graph "${details.graphId}": code schema differs from database schema`,
      "SCHEMA_MISMATCH",
      {
        details,
        category: "system",
        suggestion: `Run schema migration to update the database, or use createStoreWithSchema() for automatic migration.`,
        cause: options?.cause,
      },
    );
    this.name = "SchemaMismatchError";
  }
}

/**
 * Details for MigrationError.
 */
/**
 * Why a migration operation failed. A stable discriminant so callers can
 * branch on the outcome instead of matching the message text, which is free
 * to be reworded in any release.
 */
export const MIGRATION_FAILURE_REASONS = [
  /**
   * The persisted schema is behind the code graph. Raised on the
   * least-privilege runtime path, which cannot run DDL — a privileged
   * migrator must advance the schema first. Inspect `details.diff`
   * (`hasBreakingChanges`) to tell an additive change from an incompatible
   * one.
   */
  "schema-behind",
  /** Breaking changes were detected while `throwOnBreaking` was set. */
  "breaking-change",
  /** No active schema version exists for the graph. */
  "no-active-version",
  /** The requested schema version does not exist for the graph. */
  "version-not-found",
  /**
   * The schema about to be committed drops node or edge kinds that still
   * hold rows. Committing would make those rows unreachable, and the next
   * `materializeRemovals()` — which re-derives removals from schema history —
   * would delete them. Inspect `details.droppedKinds` for the names. Use
   * `Store.removeKinds()`, the removal path that queues cleanup explicitly,
   * or pass `discardDroppedKindRows` to `migrateSchema` if losing the rows is
   * the intent.
   */
  "kind-removal",
  /** A declared edge match identity changed while its edge kind held rows. */
  "edge-match-identity-rekey",
] as const;

export type MigrationFailureReason = (typeof MIGRATION_FAILURE_REASONS)[number];

/**
 * Structured context for a {@link MigrationError}, discriminated on `reason`.
 *
 * Modelled as a union rather than one shape with optional fields so the
 * payload a reason promises is the payload it must carry: narrowing on
 * `reason === "kind-removal"` gives you a non-optional `droppedKinds`, and the
 * constructor cannot be handed a `kind-removal` without one.
 *
 * The common fields are repeated per member rather than factored into a shared
 * base alias, which would be a hoisted-unexported symbol on the public
 * entrypoint.
 */
export type MigrationErrorDetails =
  | Readonly<{
      graphId: string;
      fromVersion: number;
      toVersion: number;
      /** Stable discriminant — branch on this, not the message. */
      reason: "schema-behind" | "breaking-change";
      /**
       * The structured diff behind the failure. Carries per-change `severity`
       * alongside `hasChanges` / `hasBreakingChanges`, so a caller can decide
       * "additive → proceed, incompatible → ask the user" without re-querying.
       */
      diff?: SchemaDiff;
    }>
  | Readonly<{
      graphId: string;
      fromVersion: number;
      toVersion: number;
      reason: "no-active-version" | "version-not-found";
    }>
  | Readonly<{
      graphId: string;
      fromVersion: number;
      toVersion: number;
      reason: "kind-removal";
      /**
       * The dropped kinds that still held rows, and so caused the refusal. A
       * kind the commit drops but that is already empty is permitted and does
       * NOT appear here. Both lists are sorted; at least one is non-empty.
       */
      droppedKinds: Readonly<{
        nodes: readonly string[];
        edges: readonly string[];
      }>;
    }>
  | Readonly<{
      graphId: string;
      fromVersion: number;
      toVersion: number;
      reason: "edge-match-identity-rekey";
      edgeKinds: readonly string[];
    }>;

/**
 * Thrown when schema migration fails.
 */
export class MigrationError extends TypeGraphError {
  declare readonly details: MigrationErrorDetails;

  constructor(
    message: string,
    details: MigrationErrorDetails,
    options?: { cause?: unknown },
  ) {
    super(message, "MIGRATION_ERROR", {
      details,
      category: "system",
      suggestion: `Review the schema changes and ensure they are backwards compatible, or implement a data migration.`,
      cause: options?.cause,
    });
    this.name = "MigrationError";
  }
}

export type BaseSchemaMigrationErrorDetails = Readonly<{
  installedVersion: number | undefined;
  requiredVersion: number;
  reason: "missing" | "stale" | "newer";
}>;

/**
 * Thrown when the deployment-wide TypeGraph base relations have not been
 * adopted to the physical schema required by this library version.
 *
 * Unlike {@link MigrationError}, this is not about one graph's serialized
 * schema document. It is the zero-DDL runtime refusal that tells an operator
 * to run privileged store preparation before attaching a DML-only role.
 */
export class BaseSchemaMigrationError extends TypeGraphError {
  declare readonly details: BaseSchemaMigrationErrorDetails;

  constructor(
    details: BaseSchemaMigrationErrorDetails,
    options?: Readonly<{ cause?: unknown }>,
  ) {
    const installed =
      details.installedVersion === undefined ?
        "no installation marker"
      : `base schema version ${String(details.installedVersion)}`;
    super(
      details.reason === "newer" ?
        `The database has ${installed}, which is newer than this TypeGraph release supports (version ${String(details.requiredVersion)}).`
      : `The database has ${installed}; this TypeGraph release requires base schema version ${String(details.requiredVersion)}.`,
      "BASE_SCHEMA_MIGRATION_REQUIRED",
      {
        category: "system",
        details,
        suggestion:
          details.reason === "newer" ?
            "Use a TypeGraph release that supports the installed base schema version."
          : "Run createStoreWithSchema() or createAdapterStoreWithSchema() once with a privileged DDL-capable database role before attaching the runtime role.",
        cause: options?.cause,
      },
    );
    this.name = "BaseSchemaMigrationError";
  }
}

/**
 * Details for EagerMaterializationError.
 */
export type EagerMaterializationErrorDetails = Readonly<{
  graphId: string;
  failedIndexNames: readonly string[];
}>;

/**
 * Thrown by `Store.evolve(extension, { eager })` when the schema
 * commit succeeded but the follow-on `materializeIndexes()` produced
 * one or more failed entries.
 *
 * Recovery: the schema commit is NOT rolled back. The new `Store` is
 * fully constructed and (when `options.ref` was supplied) `ref.current`
 * already points to it — the caller can read the new store via the ref
 * and decide how to handle the failed indexes (retry, skip, alert).
 * The full `MaterializeIndexesResult` is attached as `.materialization`.
 *
 * @example
 * ```ts
 * const ref = { current: store };
 * try {
 *   await store.evolve(extension, { ref, eager: {} });
 * } catch (error) {
 *   if (error instanceof EagerMaterializationError) {
 *     // schema is committed; ref.current is the new store
 *     log.warn(
 *       { failed: error.failedIndexNames },
 *       "indexes did not materialize; will retry",
 *     );
 *     await ref.current.materializeIndexes();
 *   } else {
 *     throw error;
 *   }
 * }
 * ```
 */
export class EagerMaterializationError extends TypeGraphError {
  declare readonly details: EagerMaterializationErrorDetails;

  /**
   * The full materialization result, including successful and failed
   * entries. Same shape as `Store.materializeIndexes()` returns.
   */
  readonly materialization: MaterializeIndexesResult;

  constructor(materialization: MaterializeIndexesResult, graphId: string) {
    const names = collectFailedIndexNames(materialization);
    super(
      `Eager materialization failed for ${names.length} index(es) on graph "${graphId}": ${names.join(", ")}. The schema commit succeeded; the new Store is available via the ref handle (when supplied) and the failed indexes can be retried via store.materializeIndexes().`,
      "EAGER_MATERIALIZATION_FAILED",
      {
        details: { graphId, failedIndexNames: names },
        category: "system",
        suggestion: `Inspect error.materialization for per-index errors. Each failure preserves any prior successful materialization timestamp; the schema is committed regardless.`,
      },
    );
    this.name = "EagerMaterializationError";
    this.materialization = materialization;
  }

  /**
   * Names of just the indexes that failed — derived from
   * `materialization.results` so the two views can never disagree.
   */
  get failedIndexNames(): readonly string[] {
    return collectFailedIndexNames(this.materialization);
  }
}

function collectFailedIndexNames(
  materialization: MaterializeIndexesResult,
): readonly string[] {
  const names: string[] = [];
  for (const entry of materialization.results) {
    if (entry.status === "failed") names.push(entry.indexName);
  }
  return names;
}

/**
 * Details for StaleVersionError. `actual` is the active version the rejecting
 * read observed, and is `0` only when the graph genuinely has no active version
 * (an initial-commit race where another writer initialized first, or a graph
 * whose schema rows were removed out of band). A concurrent advance always
 * reports the newly active version, so `0` is a reliable signal of absence
 * rather than of contention.
 */
export type StaleVersionErrorDetails = Readonly<{
  graphId: string;
  expected: number;
  actual: number;
}>;

/**
 * Thrown by `commitSchemaVersion`, `setActiveVersion`, and the schema write
 * fence a schema-managed Store write takes, when the caller's view of the
 * active schema version is out of date — another writer has already advanced
 * it.
 *
 * Recovery: re-read the active version with `getActiveSchema(graphId)`,
 * recompute against the new baseline, and retry. This is a routine
 * concurrency signal, not a bug.
 */
export class StaleVersionError extends TypeGraphError {
  declare readonly details: StaleVersionErrorDetails;

  constructor(
    details: StaleVersionErrorDetails,
    options?: { cause?: unknown },
  ) {
    super(
      `Schema version was advanced by another writer for graph "${details.graphId}": expected active version ${details.expected}, actual is ${details.actual}`,
      "STALE_SCHEMA_VERSION",
      {
        details,
        category: "system",
        suggestion: `Re-read the active schema version, recompute the new version against that baseline, and retry the commit.`,
        cause: options?.cause,
      },
    );
    this.name = "StaleVersionError";
  }
}

/**
 * Details for SchemaContentConflictError.
 */
export type SchemaContentConflictErrorDetails = Readonly<{
  graphId: string;
  version: number;
  existingHash: string;
  incomingHash: string;
}>;

/**
 * Thrown by `commitSchemaVersion` when a row already exists at the
 * target version with a *different* schema hash — i.e. two writers
 * committed materially different schemas at the same version number.
 *
 * Distinct from `StaleVersionError`: this is not a refetch-and-retry
 * situation, it's a content disagreement that needs operator
 * intervention. Typically caused by inconsistent application
 * deployments writing schemas that hash differently.
 */
export class SchemaContentConflictError extends TypeGraphError {
  declare readonly details: SchemaContentConflictErrorDetails;

  constructor(
    details: SchemaContentConflictErrorDetails,
    options?: { cause?: unknown },
  ) {
    super(
      `Schema content conflict for graph "${details.graphId}" at version ${details.version}: existing hash ${details.existingHash} differs from incoming hash ${details.incomingHash}`,
      "SCHEMA_CONTENT_CONFLICT",
      {
        details,
        category: "system",
        suggestion: `Two writers committed different schemas at the same version. Reconcile the application deployments so they produce the same canonical schema, then retry.`,
        cause: options?.cause,
      },
    );
    this.name = "SchemaContentConflictError";
  }
}

// ============================================================
// Configuration Errors (category: "user")
// ============================================================

/**
 * Thrown when graph configuration is invalid.
 *
 * This includes invalid schema definitions, ontology conflicts,
 * and other configuration issues detected at graph creation time.
 */
export class ConfigurationError extends TypeGraphError {
  constructor(
    message: string,
    details: Record<string, unknown> = {},
    options?: { cause?: unknown; suggestion?: string },
  ) {
    super(message, "CONFIGURATION_ERROR", {
      details,
      category: "user",
      suggestion:
        options?.suggestion ?? `Review your graph definition for errors.`,
      cause: options?.cause,
    });
    this.name = "ConfigurationError";
  }
}

/** The lock acquisition phase that exhausted the schema-fence wait budget. */
export type SchemaFencePhase = "schema-advisory" | "schema-row" | "writer-slot";

/** Identifies the graph, lock phase, and requested wait budget for a timeout. */
export type SchemaFenceTimeoutErrorDetails = Readonly<{
  graphId: string;
  phase: SchemaFencePhase;
  waitBudgetMs: number;
}>;

/** The caller-owned transaction could not acquire its schema fence in time. */
export class SchemaFenceTimeoutError extends TypeGraphError {
  /** The graph and lock acquisition that exceeded the caller's budget. */
  declare readonly details: SchemaFenceTimeoutErrorDetails;

  constructor(
    graphId: string,
    phase: SchemaFencePhase,
    waitBudgetMs: number,
    cause?: unknown,
  ) {
    super(
      `Timed out acquiring the schema fence for graph ${graphId}.`,
      "SCHEMA_FENCE_TIMEOUT",
      {
        category: "system",
        details: { graphId, phase, waitBudgetMs },
        suggestion:
          "Roll back the complete caller transaction before retrying it.",
        cause,
      },
    );
    this.name = "SchemaFenceTimeoutError";
  }
}

/**
 * Why a destructive contribution rebuild is refused.
 *
 * - `vector-source-unavailable` — the projection is vector storage.
 *   TypeGraph stores caller-supplied embeddings and nothing else: the
 *   vectors exist only in the table a rebuild would drop, so there is no
 *   source to reconstruct them from. Dropping anyway would silently
 *   destroy the embeddings and hand back storage that looks healthy and
 *   returns nothing. `store.reembedVectorField(kind, fieldPath, {
 *   embed })` is the sanctioned destructive path — it takes the callback
 *   that can regenerate what the drop destroys.
 * - `no-drop-ddl` — the active strategy declares no `dropDdl` for the
 *   contribution, so TypeGraph does not know how to tear its storage
 *   down. Synthesizing a `DROP TABLE` from the resolved name would
 *   guess at a teardown the strategy never sanctioned.
 * - `no-schema-fence` — the backend exposes no transactional schema
 *   fence (`schemaWriteTransaction`), so the drop, recreate, refill, and
 *   stamp could not be made atomic. Running them unfenced could leave
 *   storage attested but empty, or a concurrent schema writer
 *   interleaved with the drop.
 * - `shared-storage-in-use` — the contribution's recorded shape is stale,
 *   so only recreating its storage repairs it, but that storage is one
 *   table holding other graphs' rows as well. Their content is derived
 *   from their own nodes through their own schemas, so this process cannot
 *   put it back. A rebuild that dropped anyway would leave every other
 *   graph's search silently empty; one that re-stamped this graph's marker
 *   without the drop would bless a physical shape nothing verified.
 * - `fulltext-unavailable` — the backend declares no fulltext capability (`fulltext: false`, or no `capabilities.fulltext`),
 *   so there is no fulltext contribution to rebuild at all.
 */
export type ContributionRebuildRefusal =
  | "vector-source-unavailable"
  | "no-drop-ddl"
  | "no-schema-fence"
  | "shared-storage-in-use"
  | "fulltext-unavailable";

const CONTRIBUTION_REBUILD_REFUSAL_MESSAGE: Readonly<
  Record<ContributionRebuildRefusal, string>
> = {
  "vector-source-unavailable":
    "Vector contributions cannot be rebuilt: TypeGraph stores the vectors " +
    "callers supply and never the inputs that produced them, so the " +
    "embeddings exist only in the storage a rebuild would drop.",
  "no-drop-ddl":
    "The active strategy declares no teardown DDL for this contribution, so " +
    "its storage cannot be dropped and recreated.",
  "no-schema-fence":
    "This backend exposes no transactional schema fence, so a rebuild's " +
    "drop, recreate, refill, and marker stamp cannot be made atomic.",
  "shared-storage-in-use":
    "This contribution's storage is provisioned at a different shape than " +
    "the current declaration, and the only repair — recreating it — would " +
    "destroy fulltext content belonging to other graphs that share the same " +
    "table and can only be rebuilt from their own processes.",
  "fulltext-unavailable":
    "This backend declares no fulltext capability, so there is no " +
    "fulltext contribution to rebuild.",
};

const CONTRIBUTION_REBUILD_REFUSAL_SUGGESTION: Readonly<
  Record<ContributionRebuildRefusal, string>
> = {
  "vector-source-unavailable":
    "Run store.reembedVectorField(kind, fieldPath, { embed }) instead: it " +
    "recreates the field's storage and takes the callback that regenerates " +
    "the embeddings the recreate discards.",
  "no-drop-ddl":
    "Declare dropDdl on the contribution returned from the strategy's " +
    "ownedTables(), or drop and recreate the table out of band and then run " +
    "store.repairContributions() to re-stamp its marker.",
  "no-schema-fence":
    "Use a backend that implements schemaWriteTransaction, or perform the " +
    "drop and recreate out of band during a maintenance window and then run " +
    "store.repairContributions() followed by store.search.rebuildFulltext().",
  "shared-storage-in-use":
    "Recreate the shared table during a maintenance window with every graph " +
    "that uses it offline: drop it out of band, then run " +
    'store.rebuildContribution("fulltext") once per graph on this database. ' +
    "Each run recreates the table from the current DDL and refills that " +
    "graph's rows from its own nodes.",
  "fulltext-unavailable":
    "Recreate this backend without `fulltext: false` if fulltext search is " +
    "needed for this graph.",
};

/**
 * Thrown when `store.rebuildContribution()` cannot honor a rebuild.
 *
 * A refusal, never a partial attempt: nothing has been dropped when this
 * throws. The point is that the alternative — proceeding — destroys data
 * that cannot be reconstructed, so the operation declines instead of
 * quietly doing less than its name promises.
 */
export class ContributionRebuildUnsupportedError extends TypeGraphError {
  readonly reason: ContributionRebuildRefusal;

  constructor(
    reason: ContributionRebuildRefusal,
    details: Readonly<Record<string, unknown>> = {},
    options?: { cause?: unknown },
  ) {
    super(
      CONTRIBUTION_REBUILD_REFUSAL_MESSAGE[reason],
      "CONTRIBUTION_REBUILD_UNSUPPORTED",
      {
        details: { ...details, reason },
        category: "user",
        suggestion: CONTRIBUTION_REBUILD_REFUSAL_SUGGESTION[reason],
        cause: options?.cause,
      },
    );
    this.name = "ContributionRebuildUnsupportedError";
    this.reason = reason;
  }
}

/**
 * Thrown when an iterative graph algorithm exhausts its caller-visible round
 * budget before reaching its convergence predicate.
 */
export class GraphAlgorithmConvergenceError extends TypeGraphError {
  constructor(
    algorithm: string,
    maxIterations: number,
    options: Readonly<{ oscillating?: boolean }> = {},
  ) {
    const oscillating = options.oscillating === true;
    super(
      oscillating ?
        `${algorithm} entered a periodic label oscillation and can never converge.`
      : `${algorithm} did not converge within ${maxIterations} iterations.`,
      "GRAPH_ALGORITHM_CONVERGENCE_ERROR",
      {
        details: {
          algorithm,
          maxIterations,
          ...(oscillating ? { oscillating } : {}),
        },
        category: "user",
        suggestion:
          oscillating ?
            'Pass onMaxIterations: "return" to accept the fixed-round labeling, or exclude the oscillating component from the selection.'
          : "Increase maxIterations or reduce the selected graph before retrying.",
      },
    );
    this.name = "GraphAlgorithmConvergenceError";
  }
}

/** Stable reasons a weighted traversal can reject an edge's weight value. */
export type InvalidEdgeWeightReason =
  "missing" | "negative" | "non_numeric" | "out_of_range";

/** Details for InvalidEdgeWeightError. */
export type InvalidEdgeWeightErrorDetails = Readonly<{
  /** Id of the first offending edge, in binary-collation order. */
  edgeId: string;
  /** Kind of the offending edge. */
  edgeKind: string;
  /** The audited weight property. */
  property: string;
  /** Why the weight was rejected. */
  reason: InvalidEdgeWeightReason;
  /** The offending value, when one was present and renderable. */
  value?: string;
}>;

const EDGE_WEIGHT_REASONS: Readonly<
  Record<
    InvalidEdgeWeightReason,
    Readonly<{ message: string; suggestion: string }>
  >
> = {
  missing: {
    message: "has no value for weight property",
    suggestion:
      "Set the property on every selected edge, or provide defaultWeight for edges without it.",
  },
  negative: {
    message: "has a negative value for weight property",
    suggestion:
      "Weighted traversal requires non-negative weights; repair the edge data before retrying.",
  },
  non_numeric: {
    message: "has a non-numeric value for weight property",
    suggestion:
      "Store the weight property as a JSON number on every selected edge before retrying.",
  },
  out_of_range: {
    message: "has a value outside the supported range for weight property",
    suggestion:
      "Keep weight magnitudes within the range weightedShortestPath documents (large enough to be a normal IEEE 754 double, small enough that path sums cannot overflow).",
  },
};

/**
 * Thrown when a weighted graph algorithm finds an edge whose weight property
 * violates the weighted-traversal contract (missing without a default,
 * non-numeric, or negative). Raised before any traversal rounds run, so a
 * weighted call either observes a fully valid weight domain or fails fast.
 */
export class InvalidEdgeWeightError extends TypeGraphError {
  declare readonly details: InvalidEdgeWeightErrorDetails;

  constructor(details: InvalidEdgeWeightErrorDetails) {
    super(
      `Edge '${details.edgeId}' (kind '${details.edgeKind}') ${EDGE_WEIGHT_REASONS[details.reason].message} '${details.property}'${
        details.value === undefined ? "" : ` (value: ${details.value})`
      }.`,
      "INVALID_EDGE_WEIGHT",
      {
        details: { ...details },
        category: "user",
        suggestion: EDGE_WEIGHT_REASONS[details.reason].suggestion,
      },
    );
    this.name = "InvalidEdgeWeightError";
  }
}

/** Thrown when an operation requires a backend capability it does not expose. */
export class UnsupportedBackendCapabilityError extends TypeGraphError {
  constructor(
    operation: string,
    capability: string,
    details: Readonly<Record<string, unknown>> = {},
    suggestion?: string,
    // Appended to the generated message, never to `suggestion`: every other
    // capability-refusal gate places its batch-engine explanation
    // (`batchRefusalSuffix`) in the message, and this constructor's message
    // is otherwise fixed shape, so a caller with that explanation needs a
    // seam to reach it rather than smuggling it into the advice sentence.
    messageSuffix?: string,
  ) {
    super(
      `${operation} requires backend capability '${capability}'.${messageSuffix ?? ""}`,
      "UNSUPPORTED_BACKEND_CAPABILITY",
      {
        details: { operation, capability, ...details },
        category: "user",
        suggestion:
          suggestion ??
          "Use a backend that advertises this capability before invoking the operation.",
      },
    );
    this.name = "UnsupportedBackendCapabilityError";
  }
}

/** Stable reasons an intentionally trusted initial import can be rejected. */
export type TrustedImportErrorReason =
  | "backend_unsupported"
  | "database_not_empty"
  | "fulltext_unsupported"
  | "history_unsupported"
  | "identity_unsupported"
  | "invalid_stream"
  | "revision_tracking_unsupported"
  | "uniqueness_unsupported"
  | "vector_unsupported";

/** Thrown when the trusted initial-import contract is unavailable or violated. */
export class TrustedImportError extends TypeGraphError {
  constructor(
    message: string,
    reason: TrustedImportErrorReason,
    details: Readonly<Record<string, unknown>> = {},
    options?: Readonly<{ cause?: unknown; suggestion?: string }>,
  ) {
    super(message, "TRUSTED_IMPORT_ERROR", {
      details: { ...details, reason },
      category: "user",
      suggestion:
        options?.suggestion ??
        "Use the validating bulk or interchange import APIs unless every trusted-import precondition is satisfied.",
      cause: options?.cause,
    });
    this.name = "TrustedImportError";
  }
}

/**
 * Thrown to a graph export stream's consumer when the caller's `AbortSignal`
 * settled the stream instead of it reaching its end.
 *
 * Raised only after the export has given back everything it took, so receiving
 * it means the connection is already usable again. WHAT it took depends on the
 * backend: an export on one reporting `capabilities.execution.interactiveTransactions` holds a
 * repeatable-read snapshot (and, on a serialized connection, that connection's
 * stream lease), and both are settled before this is thrown; an export on a
 * backend without transactions holds neither, and its remaining reads are
 * simply abandoned — its already-delivered chunks were never one snapshot to
 * begin with. The message says which case applies. `cause` carries the signal's
 * own `reason` when the caller supplied one.
 */
export class ExportStreamCancelledError extends TypeGraphError {
  constructor(
    message: string,
    details: Readonly<Record<string, unknown>> = {},
    options?: Readonly<{ cause?: unknown; suggestion?: string }>,
  ) {
    super(message, "INTERCHANGE_EXPORT_STREAM_ABORTED", {
      details,
      category: "user",
      suggestion:
        options?.suggestion ??
        "Start a new export; the aborted one released its snapshot transaction and the connection it held.",
      cause: options?.cause,
    });
    this.name = "ExportStreamCancelledError";
  }
}

/**
 * Thrown when an export stream's consumer leaves a delivered chunk
 * unacknowledged for longer than its configured idle timeout.
 *
 * The timeout measures consumer idleness only: it starts when a chunk is
 * yielded and stops when the consumer asks for the next one. Receiving this
 * error means the timed-out export has settled its snapshot transaction and
 * released any serialized connection lease it held. `details.idleTimeoutMs`
 * carries the configured bound.
 */
export class ExportStreamIdleTimeoutError extends TypeGraphError {
  constructor(graphId: string, idleTimeoutMs: number, transactional: boolean) {
    super(
      transactional ?
        `The graph export stream consumer did not request its next chunk within ${idleTimeoutMs}ms: its repeatable-read snapshot has been rolled back and the connection it held released.`
      : `The graph export stream consumer did not request its next chunk within ${idleTimeoutMs}ms: its remaining reads were abandoned. This backend does not support transactions, so the export held no snapshot and no connection.`,
      "INTERCHANGE_EXPORT_STREAM_IDLE_TIMEOUT",
      {
        details: { graphId, idleTimeoutMs },
        category: "user",
        suggestion:
          transactional ?
            "Start a new export and consume each chunk within the configured idle timeout, increase idleTimeoutMs, or cancel explicitly with an AbortSignal."
          : "Start a new export and consume each chunk within the configured idle timeout, increase idleTimeoutMs, or cancel explicitly with an AbortSignal; discard the partial export unless mutually-inconsistent chunks are acceptable.",
      },
    );
    this.name = "ExportStreamIdleTimeoutError";
  }
}

/**
 * The stable `details.code` values raised by the recorded-capture guards on a
 * history- or revision-tracked store. These are the sanctioned branch points
 * for a portable caller that must pick a transaction strategy without
 * substring-matching {@link ConfigurationError} messages.
 *
 * - `RECORDED_CAPTURE_REQUIRES_CALLBACK_TRANSACTION`: `store.withTransaction`
 *   was called on a history-enabled store, which has no flush point before the
 *   caller commits — use `store.withRecordedTransaction` instead.
 * - `RECORDED_CAPTURE_RAW_SQL_DISABLED`: a raw SQL escape (`tx.sql`,
 *   `backend.executeStatement`/`executeDdl`) was reached on a history-enabled
 *   store, where it would bypass recorded-time capture.
 * - `REVISION_TRACKING_RAW_SQL_DISABLED`: the same raw SQL escape on a
 *   revision-tracked store, where it would bypass the revision anchor.
 *
 * @see isRecordedCaptureGuardError
 */
export const RECORDED_CAPTURE_GUARD_CODES = [
  "RECORDED_CAPTURE_REQUIRES_CALLBACK_TRANSACTION",
  "RECORDED_CAPTURE_RAW_SQL_DISABLED",
  "REVISION_TRACKING_RAW_SQL_DISABLED",
] as const;

/** A stable, branchable code raised by a recorded-capture guard. */
export type RecordedCaptureGuardCode =
  (typeof RECORDED_CAPTURE_GUARD_CODES)[number];

/**
 * A {@link ConfigurationError} narrowed to carry a {@link RecordedCaptureGuardCode}
 * in `details.code` — the shape {@link isRecordedCaptureGuardError} guarantees.
 * `C` narrows `details.code` to a single code when the guard was called with a
 * specific one; it defaults to the full union.
 */
export type RecordedCaptureGuardError<
  C extends RecordedCaptureGuardCode = RecordedCaptureGuardCode,
> = ConfigurationError & Readonly<{ details: Readonly<{ code: C }> }>;

function isRecordedCaptureGuardCode(
  value: unknown,
): value is RecordedCaptureGuardCode {
  // Widen the readonly tuple so `includes` accepts an `unknown` needle.
  return (RECORDED_CAPTURE_GUARD_CODES as readonly unknown[]).includes(value);
}

/**
 * Type guard for the recorded-capture guard errors, so a portable caller can
 * branch on the invariant a store enforced instead of substring-matching the
 * message. Pass `code` to narrow to a single guard; omit it to match any.
 *
 * Distinguishes "history capture forbids raw SQL here" from "this backend has
 * no transactions" (the latter carries no guard code — see
 * `TransactionContext.sqlAvailability` for the capability discriminant).
 *
 * Passing `code` also narrows `details.code` to that literal on the guarded
 * branch, so a caller can read the payload without re-checking.
 *
 * @example
 * ```typescript
 * // `withTransaction` is a compile error on a history-enabled store, so widen
 * // to the base Store surface to reach the runtime guard this branches on.
 * const store: Store<typeof graph> = historyStore;
 * try {
 *   store.withTransaction(externalTx);
 * } catch (error) {
 *   if (
 *     isRecordedCaptureGuardError(
 *       error,
 *       "RECORDED_CAPTURE_REQUIRES_CALLBACK_TRANSACTION",
 *     )
 *   ) {
 *     // error.details.code is now the literal, not the union.
 *     await historyStore.withRecordedTransaction(externalTx, run);
 *   } else {
 *     throw error;
 *   }
 * }
 * ```
 */
export function isRecordedCaptureGuardError<C extends RecordedCaptureGuardCode>(
  error: unknown,
  code: C,
): error is RecordedCaptureGuardError<C>;
export function isRecordedCaptureGuardError(
  error: unknown,
): error is RecordedCaptureGuardError;
export function isRecordedCaptureGuardError(
  error: unknown,
  code?: RecordedCaptureGuardCode,
): error is RecordedCaptureGuardError {
  if (!(error instanceof ConfigurationError)) return false;
  const actual = error.details["code"];
  if (!isRecordedCaptureGuardCode(actual)) return false;
  return code === undefined || actual === code;
}

/**
 * Why a store's strategy-owned storage is not usable on the current
 * connection. Drives the {@link StoreNotInitializedError} message and
 * is surfaced in `details.reason` for programmatic handling.
 *
 * - `missing`: no durable materialization marker exists — the database
 *   was never initialized for this graph's runtime contributions.
 * - `stale`: a marker exists but its recorded signature no longer
 *   matches the resolved contribution DDL (strategy swap or DDL drift).
 *   The hot path refuses rather than silently re-materializing.
 * - `failed`: the most recent boot-time materialization attempt
 *   recorded an error. Boot may retry; the hot path refuses.
 */
export type StoreNotInitializedReason = "missing" | "stale" | "failed";

const STORE_NOT_INITIALIZED_REASON_PHRASE: Readonly<
  Record<StoreNotInitializedReason, string>
> = {
  missing: "is not initialized",
  stale: "is stale (recorded materialization signature no longer matches)",
  failed: "failed its last initialization attempt",
};

/**
 * Details for StoreNotInitializedError. `graphId`/`reason` are always
 * present; callers may merge additional context (e.g. `logicalName`) via
 * `options.details`.
 */
export type StoreNotInitializedErrorDetails = Readonly<{
  graphId: string;
  reason: StoreNotInitializedReason;
}> &
  Readonly<Record<string, unknown>>;

/**
 * Thrown when a fulltext- or vector-dependent operation runs against a
 * connection whose strategy-owned storage has not been durably materialized.
 *
 * `createStore()` is a synchronous, zero-I/O attach: it never creates
 * tables, repairs DDL, or writes materialization markers. The durable
 * marker is written exclusively by the async boot path
 * (`createStoreWithSchema`). When a fulltext or embedding read/write — or
 * an adopted/business transaction — observes no valid marker, it refuses
 * loudly here instead of lazily emitting DDL on the hot path.
 */
export class StoreNotInitializedError extends TypeGraphError {
  declare readonly details: StoreNotInitializedErrorDetails;

  // `graphId`/`reason` are positional and required: both are
  // load-bearing for the message and for programmatic handling via
  // `details.reason`. Caller `details` merge underneath — spread first
  // so `graphId`/`reason` win and can't be clobbered by an
  // accidentally-colliding extra key.
  constructor(
    graphId: string,
    reason: StoreNotInitializedReason,
    options?: {
      cause?: unknown;
      details?: Readonly<Record<string, unknown>> &
        Readonly<{ graphId?: never; reason?: never }>;
    },
  ) {
    super(
      `${storageLabelFromLogicalName(options?.details?.["logicalName"])} for ` +
        `graph "${graphId}" ${STORE_NOT_INITIALIZED_REASON_PHRASE[reason]}. ` +
        storeNotInitializedAction(reason, options?.details?.["logicalName"]),
      "STORE_NOT_INITIALIZED",
      {
        details: { ...options?.details, graphId, reason },
        category: "user",
        suggestion:
          isStaleVectorSlot(reason, options?.details?.["logicalName"]) ?
            "The field's declared shape (e.g. its dimension) changed after " +
            "its storage was provisioned. Run store.reembedVectorField(" +
            "kind, fieldPath) to recreate the storage at the new shape and " +
            "re-embed."
          : "Call createStoreWithSchema(graph, backend) once at application " +
            "startup. createStore() attaches to an already-initialized " +
            "database and does not materialize storage itself.",
        cause: options?.cause,
      },
    );
    this.name = "StoreNotInitializedError";
  }
}

const VECTOR_LOGICAL_NAME_PREFIX = "vector:";

/**
 * A stale VECTOR slot is its own failure mode: the storage exists but was
 * provisioned at a different shape (the declared dimension changed), and
 * the fix is `store.reembedVectorField`, not a re-run of
 * `createStoreWithSchema` (whose boot pass deliberately skips drifted
 * slots). Drives the action sentence and the suggestion.
 */
function isStaleVectorSlot(
  reason: StoreNotInitializedReason,
  logicalName: unknown,
): boolean {
  return (
    reason === "stale" &&
    typeof logicalName === "string" &&
    logicalName.startsWith(VECTOR_LOGICAL_NAME_PREFIX)
  );
}

/** The actionable trailing sentence of the error message, per failure mode. */
function storeNotInitializedAction(
  reason: StoreNotInitializedReason,
  logicalName: unknown,
): string {
  if (isStaleVectorSlot(reason, logicalName)) {
    return (
      `The declared shape changed after provisioning — run ` +
      `store.reembedVectorField(kind, fieldPath) to recreate the storage ` +
      `at the new shape.`
    );
  }
  return (
    `Run createStoreWithSchema(graph, backend) during application boot, ` +
    `outside request handlers and adopted transactions, before using createStore().`
  );
}

/** Details for a runtime contribution whose durable marker outlived storage. */
export type ContributionUnavailableErrorDetails = Readonly<{
  graphId: string;
  logicalName: "fulltext";
  physicalName: string;
  state: "physical-storage-missing";
}>;

/**
 * Thrown when a gated fulltext operation discovers that materialized storage
 * disappeared after its durable marker recorded successful initialization.
 */
export class ContributionUnavailableError extends TypeGraphError {
  declare readonly details: ContributionUnavailableErrorDetails;

  constructor(
    graphId: string,
    physicalName: string,
    options?: Readonly<{ cause?: unknown }>,
  ) {
    const suggestion =
      'Run store.rebuildContribution("fulltext") to recreate the lost ' +
      "storage and repopulate it from the graph's nodes.";
    super(
      `Fulltext storage "${physicalName}" for graph "${graphId}" ` +
        `disappeared after initialization. ${suggestion}`,
      "CONTRIBUTION_UNAVAILABLE",
      {
        details: {
          graphId,
          logicalName: "fulltext",
          physicalName,
          state: "physical-storage-missing",
        },
        category: "user",
        suggestion,
        cause: options?.cause,
      },
    );
    this.name = "ContributionUnavailableError";
  }
}

/**
 * Human label for the un-materialized storage, derived from the
 * contribution `logicalName`: fulltext keeps "fulltext storage"; a vector
 * slot ("vector:&lt;kind&gt;.&lt;field&gt;") reads as `vector storage
 * "&lt;kind&gt;.&lt;field&gt;"` so the message names the exact embedding
 * field. Falls back to a neutral phrase when no logical name is supplied.
 */
function storageLabelFromLogicalName(logicalName: unknown): string {
  if (typeof logicalName !== "string") return "runtime storage";
  if (logicalName.startsWith(VECTOR_LOGICAL_NAME_PREFIX)) {
    return `vector storage "${logicalName.slice(VECTOR_LOGICAL_NAME_PREFIX.length)}"`;
  }
  if (logicalName === "fulltext") return "fulltext storage";
  return `"${logicalName}" storage`;
}

// ============================================================
// Database Errors (category: "system")
// ============================================================

/**
 * Details for DatabaseOperationError.
 */
export type DatabaseOperationErrorDetails = Readonly<{
  operation: string;
  entity: string;
  /**
   * `"duplicate_key"` means the engine refused the write because the entity's
   * identity is already taken — the shape a concurrent create of the same new id
   * produces on an engine that does not serialize the two writers. It is a
   * *classified* driver failure, so callers that own a better error for the
   * condition (the create paths, which report it as "already exists") translate
   * it rather than surfacing it.
   */
  reason?: "no_row_returned" | "duplicate_key";
  /**
   * The entities the failed statement tried to insert, carried structurally so a
   * translating caller never has to parse the driver's message for them. One
   * element for a single insert; for a batch, every row in the failing chunk —
   * the engine reports the collision without saying which row lost.
   */
  attempted?: readonly Readonly<{ kind: string; id: string }>[];
}>;

/**
 * Thrown when a database operation fails unexpectedly.
 *
 * This indicates a system-level failure in the database backend,
 * not a user-recoverable error.
 */
export class DatabaseOperationError extends TypeGraphError {
  declare readonly details: DatabaseOperationErrorDetails;

  constructor(
    message: string,
    details: DatabaseOperationErrorDetails,
    options?: { cause?: unknown },
  ) {
    super(message, "DATABASE_OPERATION_ERROR", {
      details,
      category: "system",
      suggestion: `This is a system-level database error. Check the database connection and retry the operation. If the problem persists, investigate the underlying cause.`,
      cause: options?.cause,
    });
    this.name = "DatabaseOperationError";
  }
}

// ============================================================
// Vector / Embedding Errors (category: "user")
// ============================================================

/**
 * Details for EmbeddingDimensionChangedError.
 */
export type EmbeddingDimensionChangedErrorDetails = Readonly<{
  kind: string;
  fieldPath: string;
  declaredDimensions?: number;
  storedDimensions?: number;
}>;

/**
 * Thrown when an embedding field's declared dimension no longer matches the
 * dimension of its materialized per-field storage — i.e. a field's
 * `embedding(N)` was changed to `embedding(M)`. The stored vectors are invalid
 * under the new dimension and cannot be converted, only recomputed, so this is
 * a deliberate app-driven migration: call
 * `store.reembedVectorField(kind, fieldPath, ...)` to recreate the storage at
 * the new dimension and re-embed existing rows.
 */
export class EmbeddingDimensionChangedError extends TypeGraphError {
  declare readonly details: EmbeddingDimensionChangedErrorDetails;

  constructor(
    message: string,
    details: EmbeddingDimensionChangedErrorDetails,
    options?: { cause?: unknown },
  ) {
    super(message, "EMBEDDING_DIMENSION_CHANGED", {
      details,
      category: "user",
      suggestion: `Run store.reembedVectorField("${details.kind}", "${details.fieldPath}", { embed }) to recreate the field's storage at the new dimension and re-embed existing rows.`,
      cause: options?.cause,
    });
    this.name = "EmbeddingDimensionChangedError";
  }
}

// ============================================================
// Query Errors (category: "system")
// ============================================================

/**
 * Thrown when a query predicate cannot be compiled for the target database.
 */
export class UnsupportedPredicateError extends TypeGraphError {
  constructor(
    message: string,
    details: Readonly<Record<string, unknown>> = {},
    options?: { cause?: unknown; suggestion?: string },
  ) {
    super(message, "UNSUPPORTED_PREDICATE", {
      details,
      category: "system",
      suggestion:
        options?.suggestion ??
        `This predicate may not be supported by your database backend. Check the documentation for supported predicates.`,
      cause: options?.cause,
    });
    this.name = "UnsupportedPredicateError";
  }
}

// ============================================================
// Compiler Errors (category: "system")
// ============================================================

/**
 * Thrown when a compiler invariant is violated.
 *
 * This indicates a bug in the query compiler — the compiler reached
 * a state that should be unreachable. These errors are not user-recoverable.
 */
export class CompilerInvariantError extends TypeGraphError {
  constructor(
    message: string,
    details?: Readonly<Record<string, unknown>>,
    options?: { cause?: unknown },
  ) {
    super(message, "COMPILER_INVARIANT_ERROR", {
      details: details ?? {},
      category: "system",
      suggestion: `This is an internal compiler error. Please report it as a bug with the query that triggered it.`,
      cause: options?.cause,
    });
    this.name = "CompilerInvariantError";
  }
}

// ============================================================
// Lifecycle Errors (category: "system")
// ============================================================

/**
 * Thrown when an operation is attempted on a backend that has been disposed.
 *
 * This typically occurs during runtime teardown — for example, when a
 * Cloudflare Workers test runner resets Durable Object storage while
 * the TypeGraph backend still has queued operations.
 */
export class BackendDisposedError extends TypeGraphError {
  constructor(options?: { cause?: unknown }) {
    super(
      "Backend has been disposed — the underlying database connection is no longer available",
      "BACKEND_DISPOSED",
      {
        category: "system",
        suggestion:
          "Ensure all store operations complete before calling backend.close(). " +
          "In test environments, await store teardown before resetting the database.",
        cause: options?.cause,
      },
    );
    this.name = "BackendDisposedError";
  }
}

/**
 * Thrown when a statement is issued on a transaction-scoped backend after
 * its transaction boundary has already returned.
 *
 * The usual source is a callback that lets work escape it. `Promise.all`
 * rejects on its first rejection while its siblings keep running, so
 *
 * ```typescript
 * await store.transaction(async (tx) => {
 *   await Promise.all([tx.nodes.Doc.create(a), tx.nodes.Doc.create(b)]);
 * });
 * ```
 *
 * leaves `b`'s remaining statements in flight when `a` fails. Those
 * statements have nowhere safe to go: the driver is about to emit `ROLLBACK`
 * on the same pinned connection and then hand it back to the pool, where a
 * late arrival would execute inside somebody else's transaction. TypeGraph
 * refuses them here instead.
 *
 * The error is normally invisible — `Promise.all` has already rejected with
 * the original failure, and discards this one.
 */
export class TransactionClosedError extends TypeGraphError {
  constructor(options?: { cause?: unknown }) {
    super(
      "Statement issued on a transaction-scoped backend after its transaction " +
        "boundary returned. The connection has been released; the statement was not run.",
      "TRANSACTION_CLOSED",
      {
        category: "user",
        suggestion:
          "Await every write started inside store.transaction(...) before the " +
          "callback returns. Prefer awaiting a Promise.allSettled(...) over a " +
          "Promise.all(...) whose rejection would orphan its siblings.",
        cause: options?.cause,
      },
    );
    this.name = "TransactionClosedError";
  }
}

// ============================================================
// Utility Functions
// ============================================================

/**
 * Type guard for TypeGraphError.
 *
 * @example
 * ```typescript
 * try {
 *   await store.nodes.Person.create({});
 * } catch (error) {
 *   if (isTypeGraphError(error)) {
 *     console.log(error.code, error.category);
 *   }
 * }
 * ```
 */
export function isTypeGraphError(error: unknown): error is TypeGraphError {
  return error instanceof TypeGraphError;
}

/**
 * Check if error is recoverable by user action (user or constraint error).
 *
 * User-recoverable errors can typically be resolved by:
 * - Fixing invalid input data
 * - Using different IDs or values
 * - Deleting conflicting data first
 *
 * @example
 * ```typescript
 * if (isUserRecoverable(error)) {
 *   showErrorToUser(error.toUserMessage());
 * } else {
 *   logAndAlertOps(error);
 * }
 * ```
 */
export function isUserRecoverable(error: unknown): boolean {
  if (!isTypeGraphError(error)) return false;
  return error.category === "user" || error.category === "constraint";
}

/**
 * Check if error indicates a system/infrastructure issue.
 *
 * System errors typically require:
 * - Retry logic (for transient failures)
 * - Investigation (for persistent failures)
 * - Ops team notification
 */
export function isSystemError(error: unknown): boolean {
  return isTypeGraphError(error) && error.category === "system";
}

/**
 * Check if error is a constraint violation.
 */
export function isConstraintError(error: unknown): boolean {
  return isTypeGraphError(error) && error.category === "constraint";
}

/**
 * Extract suggestion from error if available.
 */
export function getErrorSuggestion(error: unknown): string | undefined {
  return isTypeGraphError(error) ? error.suggestion : undefined;
}

/** The committed schema differs from the version expected by a checked read. */
export type SchemaChangedErrorDetails = Readonly<{
  graphId: string;
  expected: number | undefined;
  actual: number | undefined;
}>;

export class SchemaChangedError extends TypeGraphError {
  declare readonly details: SchemaChangedErrorDetails;

  constructor(details: SchemaChangedErrorDetails) {
    super(
      `Schema changed for graph "${details.graphId}": expected ${String(details.expected)}, observed ${String(details.actual)}`,
      "SCHEMA_CHANGED",
      {
        details,
        category: "system",
        suggestion:
          "Reload the reconciled schema, rebuild the query, and retry the read.",
      },
    );
    this.name = "SchemaChangedError";
  }
}
